import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useCreateMeeting } from "@/application/use-cases/meetings/use-meetings.queries";
import { usePhasesList } from "@/application/use-cases/phases/use-phases.queries";
import { findPhaseForDate } from "@/application/use-cases/phases/phase-bucketing";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { WeekSelect } from "@/presentation/components/weeks/week-select";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

export const Route = createFileRoute("/_app/meetings/new")({
  component: NewMeetingPage,
});

function NewMeetingPage() {
  const navigate = useNavigate();
  const create = useCreateMeeting();
  const phasesQ = usePhasesList();
  const [title, setTitle] = useState("");
  const [meetingAt, setMeetingAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [agenda, setAgenda] = useState("");
  const [phaseId, setPhaseId] = useState<string | null>(null);
  const [phaseTouched, setPhaseTouched] = useState(false);

  const phases = phasesQ.data ?? [];
  const inferredPhaseId = findPhaseForDate(meetingAt, phases)?.id ?? null;
  const effectivePhaseId = phaseTouched ? phaseId : inferredPhaseId;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const at = new Date(meetingAt);
      const datePart = at.toISOString().slice(0, 10);
      const titleSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      const slug = `${datePart}-${titleSlug || `meeting-${Date.now()}`}`;

      const created = await create.mutateAsync({
        title,
        slug,
        meeting_at: at.toISOString(),
        duration_minutes: null,
        meeting_type: null,
        agenda: agenda || null,
        notes: null,
        summary: null,
        action_items: [],
        phase_id: effectivePhaseId,
        recording_url: null,
        transcript_url: null,
        summary_url: null,
        presentation_html: null,
        glossary: [],
        quotes: [],
        component_deltas: [],
        stakeholder_map: [],
        confidence_flags: {},
        cross_references: {},
        flow_steps: [],
      });
      toast.success("Meeting created");
      void navigate({ to: "/meetings/$id", params: { id: created.id } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create");
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader title="New meeting" />
      <form onSubmit={onSubmit} className="flex max-w-2xl flex-col gap-4 px-6 py-6">
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label="When">
          <Input
            type="datetime-local"
            value={meetingAt}
            onChange={(e) => setMeetingAt(e.target.value)}
            required
          />
        </Field>
        <Field label="Week">
          <WeekSelect
            value={effectivePhaseId}
            onChange={(next) => {
              setPhaseTouched(true);
              setPhaseId(next);
            }}
            ariaLabel="Meeting week"
          />
        </Field>
        <Field label="Agenda">
          <Textarea value={agenda} onChange={(e) => setAgenda(e.target.value)} rows={4} />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void navigate({ to: "/meetings" })}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-[10px] tracking-wider uppercase">{label}</Label>
      {children}
    </div>
  );
}
